<template>
	<div>
		<v-card class="budget-modal-card mt-2"
		:style="isScrolled ? {
			position: 'sticky',
			top: (reportHeaderHeight + reportSecondHeaderHeight) +'px',
			left: '0',
			right: '0',
			zIndex: 9
		} : {}">
			<v-card-text class="py-4">
				<vue-file-toolbar-menu :content="menu" class="bar" />
			</v-card-text>
		</v-card>
		<v-card class="budget-modal-card">
			<v-card-text class="py-4">
				<vue-document-editor
					id="budget-editor"
					ref="reportTextEditor"
					:spellcheck="true"
					lang="es"
					:content.sync="reportTextContent"
					:zoom="zoom"
					:page_format_mm="page_format_mm"
					:page_margins="page_margins"
					:overlay="overlay"
					:display="display"/>
			</v-card-text>
		</v-card>
		<input 
				type="file" 
				ref="fileInput" 
				accept="image/*" 
				style="display: none;"
			/>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce'
import interact from 'interactjs'
import VueFileToolbarMenu from 'vue-file-toolbar-menu';
import VueDocumentEditor from 'vue-document-editor'
import {fileUploadToReport} from '@/helpers/api/files'

export default {
	name: 'ReportBudgetTextEditor',
	components: { VueDocumentEditor, VueFileToolbarMenu },
	filters: {
	},
	props: {
		report: {
			type: Object,
			required: true
		}
	},
	data: () => ({
		reportTextContent: [],
		zoom: 1,
		zoom_min: 0.10,
		zoom_max: 5.0,
		page_format_mm: [215.9, 279.4],
		page_margins: "18mm 18mm 25mm 30mm",
		display: "grid",
		font: "Archivo Narrow",
		font_size: '10pt',
		mounted: false
	}),
	computed: {
		isScrolled(){
			return this.$store.state.reports.scrolledReport > 0
		},
		reportHeaderHeight(){
			return this.$store.state.reports.reportHeaderHeight
		},
		reportSecondHeaderHeight(){
			return this.$store.state.reports.reportSecondHeaderHeight
		},
		selectedCompany(){
			return this.$store.state.companies.selectedCompany
		},
		ufValue(){
			return this.$store.state.general.dayUfValue
		},
		userInfo(){
			if(this.report.morphable?.user){
				const user = this.report.morphable?.user
				if(user && user.user && user.user.profile){
					return {
						name: user.user.profile.name,
						lastname: user.user.profile.lastname,
						second_lastname: user.user.profile.second_lastname,
						email: user.user.email || '--',
						rut: user.user.profile.rut || '--',
						phone: user.user.profile.phone || '--',
						account: true
					}
				}
				return {
					name: user.name,
					lastname: user.lastname,
					second_lastname: user.second_lastname,
					rut: user.rut || '--',
					email: user.email || '--',
					phone: user.phone || '--',
				}
			}
			return null
		},
		projectInfo(){
			let client_name = 'No Disponible'
			if(this.report.morphable?.client?.profile){
				client_name = this.report.morphable.client.profile.name + ' ' + this.report.morphable.client.profile.lastname + ' ' + this.report.morphable.client.profile.second_lastname
			}
			else if(this.getType(this.report.morphable_type) == 'crm' && this.report.morphable?.user){
				client_name = (this.userInfo.name ?? '') + ' ' +
											(this.userInfo.lastname ?? '') + ' ' +
											(this.userInfo.second_lastname ?? '')
			}
			
			let project_name = this.report.morphable?.name ?? 'Sin Detalle'
			if(this.getType(this.report.morphable_type) == 'crm'){
				if(this.report.morphable?.info?.project_detail){
					project_name = (this.report.morphable.info.project_detail ?? '') + ' - ' + (this.report.morphable.info.project_type?.name ?? '')
				}
			}
			// const project_serial_number = `P. Desarchivo:  ${ this.report.serial_id } - LP${ this.report.morphable.project_line_info?.project_line?.number } - ${ this.report.prefix?.prefix } ${ this.report.report_index }`
			const project_serial_number = `P. Desarchivo:  ${ this.report.serial?.serial } - LP${ this.report.morphable?.project_line_info?.project_line?.number } - ${ this.report.prefix?.prefix } ${ this.report.report_index }`
			return {
				client_name,
				project_name,
				project_address: this.report.morphable?.info?.address?.address ?? '--',
				project_district: this.report.morphable?.info?.address?.district?.name ?? '--',
				project_type: this.report.morphable?.info?.project_type?.name ?? '--',
				project_serial_number,
				project_zone: this.report.morphable?.info?.zone?.name ?? '--',
			}
		},
		documentVariablesValue(){
			if(this.report){
				const content = {
					'{client_name}': this.projectInfo.client_name,
					'{client_dni}': this.$options.filters.formatRut(this.report.morphable?.client?.profile.rut),
					'{client_email}': this.userInfo?.email,
					'{client_phone}': this.userInfo?.phone,
					'{client_address}': this.report.morphable?.client?.profile?.address,
					'{client_district}': this.report.morphable?.client?.profile.city?.name ?? 'N/A',
					'{address}': this.report.morphable?.info?.address?.address,
					'{district}': this.report.morphable?.info?.address?.district?.name,
					'{company_name}': this.report.morphable?.company?.name,
					'{company_rut}': this.$options.filters.formatRut(this.report.morphable?.company?.rut),
					'{company_responsible}': `${this.report.morphable?.company.user_responsible.profile.name} ${this.report.morphable?.company.user_responsible.profile.lastname} ${this.report.morphable?.company.user_responsible.profile.second_lastname}`,
					'{company_responsible_rut}': this.$options.filters.formatRut(this.report.morphable?.company.user_responsible.profile.rut),
					'{company_address}': this.report.morphable?.company?.address,
					'{client_company_name}': this.report.morphable?.client?.company_profile?.name ?? 'N/A',
					'{client_company_dni}': this.$options.filters.formatRut(this.report.morphable?.client?.company_profile?.rut ?? ''),
					'{client_company_address}': this.report.morphable?.client?.company_profile?.address ?? 'N/A',
					'{client_company_district}': this.report.morphable?.client?.company_profile?.city?.name ?? 'N/A',
					'{client_rep_name}': `${this.report.morphable?.client?.company_profile?.rep_name} ${this.report.morphable?.client?.company_profile?.rep_first_lastname} ${this.report.morphable?.client?.company_profile?.rep_second_lastname}`,
					'{client_rep_dni}': this.$options.filters.formatRut(this.report.morphable?.client?.company_profile?.rep_rut),
					'{client_rep_address}': this.report.morphable?.client?.company_profile?.rep_address,
					'{project_name}': this.projectInfo.project_name,
					'{project_address}': this.projectInfo.project_address,
					'{project_district}':this.projectInfo.project_district,
					'{project_type}': this.projectInfo.project_type,
					'{project_serial_number}': this.projectInfo.project_serial_number,
					'{project_zone}': this.projectInfo.project_zone,
					'{project_responsible_name}': `${this.report.morphable?.responsible?.profile?.name} ${this.report.morphable?.responsible?.profile?.lastname} ${this.report.morphable?.responsible?.profile?.second_lastname}`,
					'{project_responsible_email}': this.report.morphable?.responsible?.email,
					'{project_responsible_phone}': this.report.morphable?.responsible?.profile?.phone,
					'{docs_included_listing}': this.report.budget?.included_document_types?.map(doc => `<li>${doc.code} / ${doc.name}</li>`).join(''),
					'{tax_name}': this.report.budget?.tax_value.value > 0 ? '+ ' + this.report.budget?.tax_value?.name ?? 'Impuesto' : '',
				}
				return content
			}
			return null
		},
		menu () {
      return [
        { icon: "format_align_left", title: "Align left", active: this.isLeftAligned, disabled: !this.current_text_style, hotkey: this.isMacLike ? "shift+command+l" : "ctrl+shift+l", click: () => document.execCommand("justifyLeft") },
        { icon: "format_align_center", title: "Align center", active: this.isCentered, disabled: !this.current_text_style, hotkey: this.isMacLike ? "shift+command+e" : "ctrl+shift+e", click: () => document.execCommand("justifyCenter") },
        { icon: "format_align_right", title: "Align right", active: this.isRightAligned, disabled: !this.current_text_style, hotkey: this.isMacLike ? "shift+command+r" : "ctrl+shift+r", click: () => document.execCommand("justifyRight") },
        { icon: "format_align_justify", title: "Justify content", active: this.isJustified, disabled: !this.current_text_style, hotkey: this.isMacLike ? "shift+command+j" : "ctrl+shift+j", click: () => document.execCommand("justifyFull") },
        { is: "separator" },
        { icon: "format_bold", title: "Bold", active: this.isBold, disabled: !this.current_text_style, hotkey: this.isMacLike ? "command+b" : "ctrl+b", click: () => document.execCommand("bold") },
        { icon: "format_italic", title: "Italic", active: this.isItalic, disabled: !this.current_text_style, hotkey: this.isMacLike ? "command+i" : "ctrl+i", click: () => document.execCommand("italic") },
        { icon: "format_underline", title: "Underline", active: this.isUnderline, disabled: !this.current_text_style, hotkey: this.isMacLike ? "command+u" : "ctrl+u", click: () => document.execCommand("underline") },
        { icon: "format_strikethrough", title: "Strike through", active: this.isStrikeThrough, disabled: !this.current_text_style, click: () => document.execCommand("strikethrough") },
        { is: "button-color", type: "compact", menu_class: "align-center", disabled: !this.current_text_style, color: this.curColor, update_color: (new_color) => document.execCommand('foreColor', false, new_color.hex8) },
        { is: "button-color", type: "compact", menu_id: "menu-background-color-text", menu_class: "align-center", disabled: !this.current_text_style, color: this.backColor,
					update_color: (new_color) => {
						document.execCommand('hiliteColor', false, new_color.hex8)
						// const icono = document.getElementById('background-text-color-icon');
						// if (icono) {
						// 	icono.style.color = new_color.hex8;
						// }
					}
				},
				{
					html: '<div style="width: 130px; font-size: 80%;">'+this.font+'</div>',
					title: "Fuente",
					chevron: true,
					menu: this.font_menu,
					disabled: !this.current_text_style,
					menu_height: 200
				},
				{
					html: '<div style="width: 40px; font-size: 80%;">'+this.font_size+'</div>',
					title: "Font Size",
					chevron: true,
					menu: this.font_size_menu,
					disabled: !this.current_text_style,
          menu_width: 80,
					menu_height: 200
				},
        { is: "spacer" },
        { icon: "image", title: "Insertar Imagen", active: this.isNumberedList, disabled: !this.current_text_style, click: () => this.startImageSelection() },
        { icon: "format_list_numbered", title: "Numbered list", active: this.isNumberedList, disabled: !this.current_text_style, click: () => document.execCommand("insertOrderedList") },
        { icon: "format_list_bulleted", title: "Bulleted list", active: this.isBulletedList, disabled: !this.current_text_style, click: () => document.execCommand("insertUnorderedList") },
        { html: "<b>H1</b>", title: "Header 1", active: this.isH1, disabled: !this.current_text_style, click: () => document.execCommand('formatBlock', false, '<h1>') },
        { html: "<b>H2</b>", title: "Header 2", active: this.isH2, disabled: !this.current_text_style, click: () => document.execCommand('formatBlock', false, '<h2>') },
        { html: "<b>H3</b>", title: "Header 3", active: this.isH3, disabled: !this.current_text_style, click: () => document.execCommand('formatBlock', false, '<h3>') },
        { icon: "format_clear", title: "Clear format", disabled: !this.current_text_style, click () { document.execCommand('removeFormat'); document.execCommand('formatBlock', false, '<div>'); } },
        
        { is: "spacer" },

        { // Format menu
          text: this.current_format_name,
          title: "Format",
          icon: "crop_free",
          chevron: true,
          menu: this.formats.map(([text, w, h]) => {
            return {
              text,
              active: (this.page_format_mm[0] == w && this.page_format_mm[1] == h),
              click: () => { this.page_format_mm = [w, h]; }
            }
          }),
          menu_width: 80,
          menu_height: 280
        },
        // { // Margins menu
        //   text: this.current_margins_name,
        //   title: "Margins",
        //   icon: "select_all",
        //   chevron: true,
        //   menu: this.margins.map(([text, value]) => {
        //     return {
        //       text: text+" ("+value+")",
        //       active: (this.page_margins == value),
        //       click: () => { this.page_margins = value; }
        //     }
        //   }),
        //   menu_width: 200,
        //   menu_class: "align-center"
        // },
        { // Zoom menu
          text: Math.floor(this.zoom * 100) + "%",
          title: "Zoom",
          icon: "zoom_in",
          chevron: true,
          menu: [
            ["200%", 2.0],
            ["150%", 1.5],
            ["125%", 1.25],
            ["100%", 1.0],
            ["75%", 0.75],
            ["50%", 0.5],
            ["25%", 0.25]
          ].map(([text, zoom]) => {
            return {
              text,
              active: this.zoom == zoom,
              click: () => { this.zoom = zoom; }
            }
          }),
          menu_width: 80,
          menu_height: 280,
          menu_class: "align-center"
        },
        // { // Display mode menu
        //   title: "Display",
        //   icon: this.display == "horizontal" ? "view_column" : (this.display == "vertical" ? "view_stream" : "view_module"),
        //   chevron: true,
        //   menu: [{
        //     icon: "view_module",
        //     active: this.display == "grid",
        //     click: () => { this.display = "grid"; }
        //   }, {
        //     icon: "view_column",
        //     active: this.display == "horizontal",
        //     click: () => { this.display = "horizontal"; }
        //   }, {
        //     icon: "view_stream",
        //     active: this.display == "vertical",
        //     click: () => { this.display = "vertical"; }
        //   }],
        //   menu_width: 55,
        //   menu_class: "align-right"
        // }
      ]
    },
		current_format_name () {
			const format = this.formats.find(([, width_mm, height_mm]) => (this.page_format_mm[0] == width_mm && this.page_format_mm[1] == height_mm));
			return format ? format[0] : (this.page_format_mm[0]+"mm x "+this.page_format_mm[1]+"mm");
		},
		formats: () => [
			["Carta", 215.9, 279.4]
		],
		current_margins_name () {
			const margins = this.margins.find(([, margins]) => (this.page_margins == margins));
			return margins ? margins[0] : this.page_margins;
		},
		margins: () => [
			["Margen Pequeño", "5mm"],
			["Margen Normal", "15mm"],
			["Margen Medio", "20mm"],
			["Margen Angosto", "10mm 15mm"]
		],
		current_text_style () { return this.mounted ? this.$refs.reportTextEditor.current_text_style : false; },
		isLeftAligned () { return ["start", "left", "-moz-left"].includes(this.current_text_style.textAlign); },
		isRightAligned () { return ["end", "right", "-moz-right"].includes(this.current_text_style.textAlign); },
		isCentered () { return ["center", "-moz-center"].includes(this.current_text_style.textAlign); },
		isJustified () { return ["justify", "justify-all"].includes(this.current_text_style.textAlign); },
		isBold () {
			const fontWeight = this.current_text_style.fontWeight;
			return fontWeight && (parseInt(fontWeight) > 400 || fontWeight.indexOf("bold") == 0);
		},
		isItalic () { return this.current_text_style.fontStyle == "italic"; },
		isUnderline () { // text-decoration is not overridden by children, so we query the parent stack
			const stack = this.current_text_style.textDecorationStack;
			return stack && stack.some(d => (d.indexOf("underline") == 0));
		},
		isStrikeThrough () { // text-decoration is not overridden by children, so we query the parent stack
			const stack = this.current_text_style.textDecorationStack;
			return stack && stack.some(d => (d.indexOf("line-through") == 0));
		},
		isNumberedList () { return this.current_text_style.isList && this.current_text_style.listStyleType == "decimal"; },
		isBulletedList () { return this.current_text_style.isList && ["disc", "circle"].includes(this.current_text_style.listStyleType); },
		isH1 () { return this.current_text_style.headerLevel == 1; },
		isH2 () { return this.current_text_style.headerLevel == 2; },
		isH3 () { return this.current_text_style.headerLevel == 3; },
		curColor () { return this.current_text_style.color || "transparent"; },
		backColor () { return this.current_text_style.backgroundColor || "transparent"; },
		isMacLike: () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform),
    font_menu () {
      return this.font_list.map(font => {
        return {
          html: '<div style="font-size:12px;padding:0;' + (font == this.font ? 'color:green;font-weight:bold;':'') + '"">'+font+'</div>',
          // icon: (this.theme != "default" && this.font == font) ? 'check' : false,
          active: (this.font == font),
					padding: 0,
          click: () => {
            document.execCommand("fontName", false, font);
            this.font = font;
          }
        };
      });
    },
    font_list: () => [
			"Archivo Narrow",
			"Andale Mono",
			"Arial",
			"Arial Black",
			"Book Antiqua",
			"Comic Sans MS",
			"Courier New",
			"Georgia",
			"Helvetica",
			"Impact",
			"Symbol",
			"Tahoma",
			"Terminal",
			"Times New Roman",
			"Trebuchet MS",
			"Verdana",
			"Webdings",
			"Wingdings"
		],
    font_size_menu () {
			let fontSizes = []
			for (let fontSize = 1; fontSize <= 40; fontSize++) {
				const size_str = fontSize + 'pt'
				fontSizes.push({
          html: '<div style="font-size:12px;padding:0;' + (size_str == this.font_size ? 'color:green;font-weight:bold;':'') + '"">'+size_str+'</div>',
          icon: (this.theme != "default" && this.font == size_str) ? 'check' : false,
          active: (this.font_size == size_str),
          click: () => {
            this.setSelectionFontSize(size_str)
            this.font_size = size_str;
          }
        })
			}
			return fontSizes
    }

	},
	watch:{
		reportTextContent(newValue, oldValue){
			if(newValue != oldValue){
				this.checkForNewImages(newValue)
				// console.log('content change: ', this.checkForNewImages(newValue))
			}
		}
	},
	mounted(){
		this.setContent(this.report.report_html)
		this.$nextTick(() => {
			this.$refs.reportTextEditor.$el.addEventListener('click', function(event) {
				if (event.target.classList.contains('image-wrapper')) {

					// const selection = window.getSelection();
					// const range = document.createRange();
					// range.selectNode(event.target); // Selecciona el nodo de la imagen
					// selection.removeAllRanges(); // Elimina cualquier selección previa
					// selection.addRange(range); // Añade la nueva selección
					// Si la imagen ya tiene la clase 'selected-image', la eliminamos
					const selectedImage = event.target;
					selectedImage.classList.toggle('selected-image'); // Cambiar la clase (agregar o quitar)
					
					// También puedes asegurarte de que sólo se seleccione una imagen a la vez
					const images = document.querySelectorAll('.resizable-box');
					images.forEach(img => {
						if (img !== selectedImage) {
							img.classList.remove('selected-image'); // Eliminar la clase de otras imágenes
						}
					});
				}
			})
		})
	},
	methods: {
		transformPlaceholdersToContentView(content) {
			let result = content;
			for (const [placeholder, label] of Object.entries(this.documentVariablesValue)) {
				const regex = new RegExp(placeholder, 'g');
				let style_text = "font-weight:bold;text-transform: uppercase;";
				if(placeholder == '{tax_name}' || placeholder == '{docs_included_listing}'){
					style_text = "";
				}
				result = result.replace(regex, `<span id="${placeholder}" style="${style_text}">${label}</span>`);
			}
			return result;
		},
		reverseContentViewToPlaceholders(content) {
			let result = content;
			result = result.replace(/<span id="(.*?)"[^>]*>(.*?)<\/span>/g, (match, placeholder) => {
					return `{${placeholder}}`;
			})
			return result;
		},
		interactjsInit(){
			const self = this
			interact('.resizable-box')
				.resizable({
					// resize from all edges and corners
					edges: { left: true, right: true, bottom: true, top: true },

					listeners: {
						move (event) {
							var target = event.target
							var x = (parseFloat(target.getAttribute('data-x')) || 0)
							var y = (parseFloat(target.getAttribute('data-y')) || 0)

							// update the element's style
							target.style.width = event.rect.width + 'px'
							target.style.height = event.rect.height + 'px'

							// translate when resizing from top or left edges
							x += event.deltaRect.left
							y += event.deltaRect.top

							target.style.transform = 'translate(' + x + 'px,' + y + 'px)'

							target.setAttribute('data-x', x)
							target.setAttribute('data-y', y)
							self.forceContentReload()
						}
					},
					modifiers: [
						interact.modifiers.aspectRatio({ ratio: 'preserve' }),
						// keep the edges inside the parent
						// interact.modifiers.restrictEdges({
						// 	outer: 'parent'
						// }),

						// minimum size
						interact.modifiers.restrictSize({
							min: { width: 100, height: 50 }
						})
					],

					inertia: true
				})
				.draggable({
					listeners: {
						start() {
							// Evento cuando comienza el arrastre
							console.log('Arrastre iniciado');
						},
						move(event) {
							var target = event.target;
							var x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
							var y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

							// Aplica la transformación para mover el elemento
							target.style.transform = 'translate(' + x + 'px,' + y + 'px)';

							// Guarda las posiciones actuales
							target.setAttribute('data-x', x);
							target.setAttribute('data-y', y);
							self.forceContentReload()
						},
						end() {
							// Evento cuando termina el arrastre
							console.log('Arrastre finalizado');
						}
					},
					inertia: true,
					modifiers: [
						interact.modifiers.snap({
							targets: [
								interact.snappers.grid({ x: 30, y: 30 })
							],
							range: Infinity,
							relativePoints: [ { x: 0, y: 0 } ]
						}),
						interact.modifiers.restrictRect({
							restriction: 'parent',
							endOnly: true
						})
					]
				})
		},
		startImageSelection(){
			const input = this.$refs.fileInput;
			input.onchange = async (event) => {
				const file = event.target.files[0];
				const formData = new FormData();
				formData.append('file', file);
				try {
					const uploaded_file = await fileUploadToReport(formData);
					if (uploaded_file.code === 200) {
						const url = uploaded_file.url;

						document.execCommand("insertHTML", false, `<img src='${url}' alt='img' style='max-width:100%;'>`)
					} else {
						console.error('Error al subir la imagen:', uploaded_file.message);
					}
				} catch (error) {
					console.error('Error en la subida de la imagen:', error);
				}
			};

			// Simular un clic en el input para abrir el selector de archivos
			input.click();
		},
		checkForNewImages: _debounce(function() { this.wrapImagesWithResizableClassSecond() }, 100),
		forceContentReload: _debounce(function() { document.execCommand('formatBlock', false, '<div>') }, 300),
		wrapImagesWithResizableClassSecond() {
			let changedContent = false;
			const newContent = this.reportTextContent.map(html => {
					const container = document.createElement('div');
					container.innerHTML = html;

					const images = container.querySelectorAll('img');

					images.forEach(img => {
						console.log('image')
							if (!img.closest('.image-wrapper')) {
									const wrapper = document.createElement('div');
									wrapper.className = 'image-wrapper';
									wrapper.setAttribute('contenteditable', 'false');

									// Aplica estilos para asegurar que el div tenga un tamaño y que la imagen se ajuste
									wrapper.style.position = 'relative';
									wrapper.style.width = '300px';  // Puedes usar un valor inicial o dinámico
									wrapper.style.height = '200px'; // Igual aquí
									wrapper.style.display = 'inline-block';

									const clonedImg = img.cloneNode(true);
									clonedImg.draggable = false;

									// Estilos para que la imagen llene el contenedor
									clonedImg.style.width = '100%';
									clonedImg.style.height = '100%';
									clonedImg.style.objectFit = 'fill'; // o 'cover' si prefieres recortar

									wrapper.appendChild(clonedImg);
									img.parentNode.replaceChild(wrapper, img);

									changedContent = true;
							}
					});

					return container.innerHTML;
			});

			if (changedContent) {
					console.log('changedContent: ', newContent);
					this.reportTextContent = newContent;
					this.interactjsInit(); // Asegúrate que interactjsInit() permite redimensionar el wrapper
			}
		},
		wrapImagesWithResizableClass() {
				let changedContent = false;
				const newContent = this.reportTextContent.map(html => {
						// Crear un contenedor DOM temporal
						const container = document.createElement('div');
						container.innerHTML = html;

						// Seleccionar todas las <img> que NO estén dentro de un div con contenteditable="false"
						const images = container.querySelectorAll('img');

						images.forEach(img => {
								if (!img.classList.contains('resizable-box')) {
										// Agregar la clase 'resizable-box' directamente a la imagen
										// img.setAttribute('contenteditable', 'false');
										img.classList.add('resizable-box');
										img.draggable = false
										img.style.maxWidth = '100%';

										changedContent = true;
								}
						});

						return container.innerHTML;
				});

				if (changedContent) {
						console.log('changedContent: ', newContent);
						this.reportTextContent = newContent;
						this.interactjsInit()
				}
		},
		wrapImagesWithResizableDiv() {
			let changedContent = false;
			const newContent = this.reportTextContent.map(html => {
				const container = document.createElement('div');
				container.innerHTML = html;

				const images = container.querySelectorAll('img');

				images.forEach(img => {
					let current = img;
					let parent = img.parentElement;

					// Si ya está dentro de un div redimensionable, saltar
					if (
						parent.tagName === 'DIV' &&
						parent.getAttribute('contenteditable') === 'false' &&
						parent.style.resize
					) return;

					// Crear wrapper
					const wrapper = document.createElement('div');
					wrapper.setAttribute('contenteditable', 'false');
					wrapper.style.resize = 'both';
					wrapper.style.overflow = 'hidden';
					wrapper.style.display = 'inline-block';
					wrapper.style.border = '1px dashed #aaa';
					wrapper.style.margin = '4px';

					const newImg = img.cloneNode(true);
					newImg.style.width = '100%';
					newImg.style.height = '100%';
					newImg.style.objectFit = 'cover';

					wrapper.appendChild(newImg);

					// Subir hasta encontrar un contenedor válido
					let hostContainer = parent;
					while (hostContainer && ['STRONG', 'EM', 'SPAN', 'U', 'SMALL', 'B', 'I'].includes(hostContainer.tagName)) {
						current = hostContainer;
						hostContainer = hostContainer.parentElement;
					}

					if (hostContainer) {
						hostContainer.replaceChild(wrapper, current);
						changedContent = true;
					}
				});

				return container.innerHTML;
			});

			if (changedContent) {
				this.reportTextContent = newContent;
			}
		},
		// wrapImagesWithResizableDiv() {
		// 	let changedContent = false
		// 	const newContent = this.reportTextContent.map(html => {
		// 		// Crear un contenedor DOM temporal
		// 		const container = document.createElement('div');
		// 		container.innerHTML = html;

		// 		// Seleccionar todas las <img> que NO estén dentro de un div con contenteditable="false"
		// 		const images = container.querySelectorAll('img');

		// 		images.forEach(img => {
		// 			const parent = img.parentElement;

		// 			// Verifica si la imagen ya está envuelta en un div con contenteditable="false"
		// 			const isAlreadyWrapped =
		// 				parent.tagName === 'DIV' &&
		// 				parent.getAttribute('contenteditable') === 'false' &&
		// 				parent.style.resize;

		// 			if (!isAlreadyWrapped) {
		// 				// Crear un nuevo wrapper div
		// 				const wrapper = document.createElement('div');
		// 				wrapper.setAttribute('contenteditable', 'false');
						
		// 				wrapper.style.resize = 'both';
		// 				wrapper.style.overflow = 'hidden';
		// 				wrapper.style.display = 'inline-block';
		// 				wrapper.style.border = '1px dashed #aaa';

		// 				// Clonar la imagen y ajustar estilo
		// 				const newImg = img.cloneNode(true);
		// 				newImg.style.width = '100%';
		// 				newImg.style.height = '100%';
		// 				newImg.style.objectFit = 'cover';

		// 				wrapper.appendChild(newImg);

		// 				// Reemplazar la imagen original con el nuevo contenedor
		// 				parent.replaceChild(wrapper, img);
		// 				changedContent = true
		// 			}
		// 		});

		// 		return container.innerHTML;
		// 	});


		// 	if(changedContent){
		// 		console.log('changedContent: ', newContent)
		// 		this.reportTextContent = newContent
		// 	}
		// },
    overlay () {
      let html = `
					<div class="budget-footer" style="margin: 0 18mm 0 30mm;;">
						<div class="company-footer-brand">
							<span style="color: #FE0000">TOLEDO</span><span style="color: #939393">ESTUDIO</span><span style="color: #504F4D">ARQUITECTOS</span>
						</div>
						<div class="company-contact">
							<p class="company-first-line"><b>CONTACTO:</b> ${this.selectedCompany.company.phone} - ${this.selectedCompany.company.email} - ${this.selectedCompany.company.address}, ${this.selectedCompany.company.city.name}, ${this.selectedCompany.company.city.country.name}</p>
							<a class="company-second-line" href="${this.selectedCompany.company.url}" target="_blank">${this.selectedCompany.company.url.replace('https://', '').replace('http://', '')}</a>
						</div>
					</div>`
      return html;
    },
		editHighlightButton(){
			const highlightElement = document.getElementById('menu-background-color-text');

			if (highlightElement && highlightElement.parentNode) {
				const parent = highlightElement.parentNode;

				// Eliminar el div hermano con clase "color-square"
				const colorSquare = parent.querySelector('.color-square');
				if (colorSquare) {
					colorSquare.remove();
				}

				// Crear e insertar el nuevo icono después de color-highlight
				const newElement = document.createElement('span');
				newElement.classList.add('material-icons');
				newElement.id = 'background-text-color-icon'
				newElement.textContent = 'format_color_fill';

				parent.insertBefore(newElement, highlightElement.nextSibling);
			}
		},
		setContent(initialContentHtml = null, fromTemplate = false){
			let content_final = '<div id="report-system-info-container">'
			content_final += `<div class="d-flex justify-space-between" contenteditable="false">
					<div class="d-flex justify-center align-items-center flex-grow-1"
						style="background-image: url(${this.selectedCompany.company.logo});background-size: contain;background-repeat: no-repeat;">
					</div>
					<div class="text-end">
						<p class="font-weight-bold my-0" style="font-size: 14pt;">
							${this.selectedCompany.company.name}
						</p>
						<p class="my-0" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;">${this.selectedCompany.company.description}</p>
						<p class="my-0" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;">Rut: ${this.$options.filters.formatRut(this.selectedCompany.company.rut)}</p>
						<p class="my-0" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;">${this.selectedCompany.company.phone}</p>
						<p class="mt-0 mb-2" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;">${this.selectedCompany.company.email}</p>
						<p class="my-0" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;"><span id="{project_serial_number}">${ this.projectInfo.project_serial_number }</span></p>
						<p class="my-0" style="font-size: 10pt;line-height: 11pt;white-space: nowrap;">Fecha Creación: ${ this.$options.filters.toDate(this.report.created_at) }</p>
					</div>
				</div>
				<br/>
				<p class="text-center font-weight-bold mb-6" style="font-size: 14pt">${ this.report.title ? this.report.title:'Presupuest Desarchivo'}</p>
				<p class="text-left mb-0"><b>Proyecto</b>: <span id="{project_name}">${ this.projectInfo.project_name }</span></p>
				<p class="text-left mb-4"><b>Dirección</b>: <span id="{project_address}">${ this.projectInfo.project_address }</p>
				<table class="mt-4 mb-4" style="width: 100%;padding: 0px 0;background-color: white;" cellspacing="0" cellpadding="0" contenteditable="false">
					<tr>
						<td class="pa-2" style="border:1px solid #cecece;width:50%;vertical-align: top;">
							<p class="font-weight-bold text-left mb-1">Cliente</p>
							<p class="text-left mb-0">Nombre: <span id="{client_name}">${ this.projectInfo.client_name }</span></p>
							<p class="text-left mb-0">Teléfono: <span id="{client_email}">${ this.userInfo?.phone ?? '--' }</span></p>
							<p class="text-left mb-0">Email: <span id="{client_phone}">${ this.userInfo?.email }</span></p>
						</td>
						<td class="pa-2" style="border:1px solid #cecece;width:50%;vertical-align: top;">
							<p class="font-weight-bold text-left mb-1">Profesional Encargado</p>
							<p class="text-left mb-0">Nombre: <span id="{project_responsible_name}">${ this.report.morphable?.responsible?.profile?.name + ' ' + this.report.morphable?.responsible?.profile?.lastname + ' ' + this.report.morphable?.responsible?.profile?.second_lastname}</span></p>
							<p class="text-left mb-0">Teléfono: <span id="{project_responsible_email}">${ this.report.morphable?.responsible?.profile.phone }</span></p>
							<p class="text-left mb-0">Email: <span id="{project_responsible_phone}">${ this.report.morphable?.responsible?.email }</span></p>
						</td>
					</tr>
				</table>
				`
			content_final += '</div>'
			if(initialContentHtml && fromTemplate){
				content_final += this.transformPlaceholdersToContentView(initialContentHtml)
			}
			else if(initialContentHtml){
				content_final += this.cleanHtmlArray(initialContentHtml)
			}
			else{
				content_final += '</br></br>'
			}

			this.reportTextContent = [content_final]
			this.$nextTick(() => {
				this.$store.commit('reports/setReportEditorRef', this.$refs.reportTextEditor)
				this.mounted = true
				this.editHighlightButton()
				this.interactjsInit()
			})
		},
		getType(type){
			switch (type) {
				case "App\\Models\\BotConversation":
					return 'crm'
				case "App\\Models\\PreProject":
					return 'preproject'
				case "App\\Models\\Project":
					return 'project'
				default:
					break;
			}
			return 'Otro'
		},
		sectionIndex(index){
			return '' +  this.$store.state.general.romanNumerals[index] + '. ' + this.$store.state.general.ordinalsNumbers[index] + ':'
		},
		setSelectionFontSize(size) {
			const selection = window.getSelection();
			if (!selection.rangeCount || selection.isCollapsed) return;

			const range = selection.getRangeAt(0);
			const extracted = range.extractContents();
			this.removeFontSizeRecursively(extracted);

			const span = document.createElement("span");
			span.style.fontSize = size;
			span.appendChild(extracted);

			range.insertNode(span);

			selection.removeAllRanges();
			const newRange = document.createRange();
			newRange.selectNodeContents(span);
			selection.addRange(newRange);
			document.execCommand('unlink')
		},
		removeFontSizeRecursively(fragment) {
			const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT, null);
			const toClean = [];

			while (walker.nextNode()) {
				const node = walker.currentNode;
				if (node.nodeName === "SPAN" && node.style.fontSize) {
					toClean.push(node);
				}
			}

			toClean.forEach(node => {
				node.style.fontSize = null;
				if (node.getAttribute("style") === "") {
					const parent = node.parentNode;
					while (node.firstChild) {
						parent.insertBefore(node.firstChild, node);
					}
					parent.removeChild(node);
				}
			});
		},
		// setSelectionFontSize(size) {
		// 	const selection = window.getSelection()
		// 	if (!selection.rangeCount) return
		// 	const range = selection.getRangeAt(0)

		// 	const fragment = range.cloneContents()
		// 	const selectedNodes = fragment.childNodes

		// 	selectedNodes.forEach(node => {
		// 		if (node.nodeType === 1) {
		// 			node.style.fontSize = size
		// 		} 
		// 		else if (node.nodeType === 3) {
		// 			const span = document.createElement("span")
		// 			span.style.fontSize = size
		// 			span.appendChild(node.cloneNode(true))
		// 			node.parentNode.replaceChild(span, node)
		// 		}
		// 	});
		// 	range.deleteContents()
		// 	range.insertNode(fragment)
		// },
		mergeAdjacentSpans(span) {
			const prev = span.previousSibling;
			const next = span.nextSibling;

			if (prev && prev.nodeType === 1 && prev.tagName === "SPAN" && prev.style.fontSize === span.style.fontSize) {
				prev.innerHTML += span.innerHTML;
				span.remove();
			}
			
			if (next && next.nodeType === 1 && next.tagName === "SPAN" && next.style.fontSize === span.style.fontSize) {
				span.innerHTML += next.innerHTML;
				next.remove();
			}
		},
		justifySelection(align){
			const selection = window.getSelection();
			if (!selection.rangeCount) return;

			const range = selection.getRangeAt(0);
			const span = document.createElement("span");
			span.style.display = "inline-block"; // Permite aplicar text-align
			span.style.textAlign = align;
			span.appendChild(range.extractContents()); // Extrae el contenido seleccionado
			range.insertNode(span);
		},
		setTemplate(template){
			this.setContent(template.content.replace(/\\"/g, '"'), true)
		},
		cleanHtmlArray(htmlArray) {
			try {
				const cleanedArray = htmlArray.map(html => {
					const parser = new DOMParser();
					const doc = parser.parseFromString(html, 'text/html');
					const divToRemove = doc.querySelector('#report-system-info-container');
					if (divToRemove) {
						divToRemove.remove();
					}
					return this.transformPlaceholdersToContentView( doc.body.innerHTML );
				});

				return cleanedArray;
			} catch (error) {
				console.error("Error al parsear el string JSON:", error.message);
				return [];
			}
		}
	}
};
</script>

<style>
	#budget-editor{
		font-size: 10pt;
		text-transform: none;
	}
	#budget-editor div,
	#budget-editor p,
	#budget-editor span{text-transform: none;}
	#budget-editor img{
		max-width: 100%;
		object-fit: contain;
	}
	.budget-footer{
		position: absolute;
		bottom: 7mm;
		left: 0;
		right: 0;
	}
	.company-footer-brand{
		background-color: #BFBFBF;
		font-weight: bold;
		text-align: center;
	}
	.budget-footer .company-contact{
			text-align: center;
		}
	.budget-footer .company-first-line{
		margin: 0;
		padding-top: 2pt;
		font-size: 10pt;
		line-height: 10pt;
		text-align:center;
	}
	.budget-footer .company-second-line{
		margin: 0;
		text-align:center;
		font-size: 10pt;
		line-height: 10pt;
		width: 100%;
		font-weight: bold;
		color: #504F4D;
		text-decoration: none;
	}
	#budget-editor table {
		border-collapse: collapse;
		vertical-align: middle;
	}
	#budget-editor th {
		background: #ccc;
	}

	#budget-editor th, #budget-editor td {
		border: 1px solid #ccc;
		padding: 4px 8px;
		vertical-align: middle;
	}

	#budget-editor tr:nth-child(even) {
		background: #efefef;
	}

	#budget-editor tr:hover {
		background: #d1d1d1;
	}

	#budget-editor p {
		margin-bottom: 0;
	}

	#budget-editor .resizable-box.selected-image {
		position: relative;
		border: 2px solid grey;
		background-color: black;
		opacity: 0.7;
	}
	#budget-editor .image-wrapper{
		content: '';
		background-color: grey;
		vertical-align: top;
	}
	
	/deep/.font-selector-custom .bar-menu-item{
		font-size: 12px;
	}
</style>